/**
 * Hausa.
 *
 * First pass. Awaiting review by a native speaker with clinical vocabulary —
 * `reviewed: false` is deliberate and should gate any clinical release.
 *
 * Written in Boko (Latin) orthography with the hooked letters ɓ, ɗ, ƙ and the
 * apostrophe for ʼy, since that is what is used in print and on phones across
 * northern Nigeria. A reviewer working from Ajami will want to say so.
 *
 * "Auna" (to measure) is used for a reading rather than a borrowed clinical
 * term, and "bugun zuciya" (the beating of the heart) rather than a transliterated
 * "pulse" — this screen is read by people deciding whether to trust us.
 */

export default {
  code: 'ha',
  intl: 'ha-NG',
  name: 'Hausa',
  endonym: 'Hausa',
  reviewed: false,
  strings: {
    'app.tagline': 'Yana karanta bugun zuciyarka daga kyamara — kuma yana faɗin gaskiya idan bai iya gani ba.',
    'app.disclaimer': 'Samfurin aiki. Ba na’urar likita ba ce, ba a tabbatar da ita ba, kuma ba gano cuta ba ne.',
    'app.skip': 'Tsallake zuwa babban abun ciki',
    'app.language': 'Harshe',
    'app.install': 'Sanya Ori a wannan na’urar',
    'app.update.title': 'Sabon salon Ori ya shirya',
    'app.update.body':
      'Za a yi amfani da shi lokaci na gaba da ka buɗe manhajar, ko kuma ka canza yanzu. Babu abin da ka ajiye da zai shafa.',
    'app.update.action': 'Sabunta yanzu',
    'app.queued': 'Auna {count} suna jiran aikawa',
    'app.queued.one': 'Auna 1 tana jiran aikawa',

    'nav.start': 'Menene wannan',
    'nav.scan': 'Yi auna',
    'nav.ori': 'Ori naka',
    'nav.record': 'Tarihinka',
    'nav.consent': 'Abin da kake rabawa',
    'nav.care': 'Inda za ka je',
    'nav.pledge': 'Alkawarin Fata',

    'status.deviceKey': 'An ajiye tarihi a wannan na’urar',
    'status.noKey': 'Babu maɓallin na’ura',
    'status.preparingKey': 'Ana shirya maɓallin na’ura…',
    'status.corpusLive': 'Haɗaɗɗen tarin auna · yana aiki',
    'status.corpusOffline': 'Haɗaɗɗen tarin auna · babu haɗi',
    'status.corpusUnset': 'Ba a saita tarin auna ba',
    'status.sharing': 'Ana raba auna',
    'status.notSharing': 'Ba a raba',

    'start.eyebrow': 'OmniAlera Ori',
    'start.title.line1': 'Bugun zuciyarka,',
    'start.title.line2': 'da haske ke karantawa',
    'start.plain1': 'Zuciyarka tana turo jini ta fuskarka. Kowane turi yana sa fatarka ta ɗan yi ja kaɗan.',
    'start.plain2': 'Ƙanƙanta ce sosai idonka ba zai gani ba. Amma kyamara na iya gani.',
    'start.plain3': 'Don haka ka dubi kyamara na ɗan daƙiƙa kaɗan, Ori kuma ya ƙidaya bugun zuciyarka.',
    'start.plain3b': 'Babu agogo. Babu waya. Babu abin siya.',
    'start.problem': 'Ga matsalar',
    'start.step1.lead': 'Fata tana da launi.',
    'start.step1.body': 'Fata mai duhu tana shan yawancin hasken da kyamara ke nema, don haka siginar ta yi ƙasa.',
    'start.step2.lead': 'Yawancin manhajoji ba su kula da wannan ba.',
    'start.step2.body': 'Suna nuna maka lamba duk da haka. Tana iya zama kuskure sosai alhali tana kama da tabbatacciya.',
    'start.step3.lead': 'Ori ba ya yin zato.',
    'start.step3.body': 'Idan bai iya ganin bugun zuciyarka a fili ba, yana gaya maka. Wannan ce dukkan bambancin.',
    'start.step4.lead': 'Za ka iya taimakawa a gyara shi.',
    'start.step4.body': 'Kowace auna da ka zaɓi rabawa — har ma wadda ta gaza — tana koya wa sabon salo yadda zai ga fata kamar taka. Ana biyan ka, kuma za ka iya janyewa a kowane lokaci.',
    'start.before.title': 'Kafin aunarka ta farko',
    'start.before.body': 'Zaɓi murabba’in da ya fi kama da fatarka. Yawancin manhajojin lafiya ba sa tambaya ko kaɗan. Shi ne abu ɗaya da ke tantance ko aunawar za ta yi aiki ko a’a.',
    'start.pledge.title': 'Ka yi Alkawarin Fata',
    'start.pledge.body': 'Bar kowace auna — ba tare da wani abu da ke nuna ko wanene kai ba — ta shiga tarin bayanan da ke koya wa waɗannan na’urori ganin fata mai duhu. Za ka iya kallon lambar tana ƙaruwa, kuma ka kashe wannan a duk lokacin da ka ga dama.',
    'start.pledge.earn': 'Yana samun kiredit 25 kan kowace auna',
    'start.cta.ready': 'Yi min auna ta farko',
    'start.cta.needTone': 'Fara zaɓi launin fata',
    'start.cta.counter': 'Ka fara ganin lambar',
    'tone.lightest': 'Mafi haske',
    'tone.darkest': 'Mafi duhu',
    'tone.chosen': 'Launi {n} cikin 10',
    'tone.none': 'Ba a zaɓa ba tukuna',
    'tone.label': 'Launin fata {n} cikin 10',
    'tone.group': 'Launin fatarka',

    'scan.eyebrow': 'Yi auna',
    'scan.title.idle': 'Ka natsu ka kuma dubi kyamara',
    'scan.title.running': 'Ana karanta ka yanzu — ka natsu',
    'scan.title.locked': 'An samu',
    'scan.title.failed': 'Ori bai iya karanta ka ba',
    'scan.intro':
      'Ori yana kallon sassa uku na fuskarka a lokaci ɗaya — goshi da kumatu biyu — kuma yana karanta kowanne ta hanyoyi biyu daban. Yana ba ka lamba ne kawai idan dukkansu sun yarda.',
    'scan.start': 'Fara',
    'scan.running': 'Ana karantawa…',
    'scan.again': 'Sake auna',
    'scan.useCamera': 'Yi amfani da kyamarata',
    'scan.useSimulated': 'Koma ga samfurin gwaji',
    'scan.privacy':
      'Kyamara kai tsaye, a ainihin lokaci. Babu abin da kake gani da ke barin wannan na’urar — aunawar tana faruwa a cikin burauzarka, kuma ba a taɓa ɗora hoto ko ɗaya ba.',
    'scan.simulated':
      'Samfurin gwaji a launin fata {tone}, an ƙirƙira shi sau huɗu da sauri fiye da ainihin lokaci don kada ka jira. Kusan kashi {attenuation}% na siginar bugun zuciya ke tsira a wannan launin.',
    'scan.skipped': 'An tsallake hotuna {count} — Ori ya gwammace jira da yin zato.',
    'scan.skipped.one': 'An tsallake hoto 1 — Ori ya gwammace jira da yin zato.',
    'scan.guess': 'Mafi kyawun zato yanzu: {bpm} bpm — ba a tabbatar ba tukuna.',
    'scan.guess.disagree': 'Hanyoyin biyu ba su yarda ba, don haka Ori na ci gaba da nema.',
    'scan.guess.settling': 'Ana jiran ya daidaita.',

    'stat.elapsed': 'Lokaci',
    'stat.clarity': 'Bayyana',
    'stat.agreement': 'Yardar sassa',
    'stat.pulse': 'Bugun zuciya',
    'stat.breathing': 'Numfashi',
    'stat.variability': 'Bambanci',
    'unit.bpm': 'bpm',
    'unit.perMin': '/minti',
    'unit.ms': 'ms',
    'unit.s': 'daƙ',

    'advice.ok': 'Yana da kyau — ka natsu.',
    'advice.no_face': 'Ba a sami fuska ba — ka kawo kanka tsakiya.',
    'advice.too_dark': 'Duhu ne sosai. Ka fuskanci taga ko fitila.',
    'advice.dim': 'Akwai ɗan duhu — ƙarin haske zai sa ya yi sauri.',
    'advice.clipped': 'Haske ya yi yawa — ka juya daga hasken.',
    'advice.bright': 'Haske ya yi yawa sosai. Ka gwada juyawa kaɗan.',
    'advice.motion': 'Motsi ya yi yawa — ka natsu.',
    'advice.slight_motion': 'Ka yi ƙoƙarin natsuwa.',

    'card.share': 'Raba wannan aunar',
    'card.sharing': 'Ana shirya…',
    'card.downloaded': 'An ajiye a na’urarka.',
    'card.failed': 'Ba a iya yin katin a wannan na’urar ba.',
    'card.pulse': 'Bugun zuciya',
    'card.breathing': 'Numfashi',
    'card.variability': 'Bambanci',
    'card.time': 'Lokacin da aka ɗauka',
    'card.regions': 'Sassan da suka yarda',
    'card.methods': 'Bambancin hanyoyi',
    'card.tone': 'Launin fata',
    'card.trusted': 'An tabbatar ta sassa uku da hanyoyi biyu',
    'card.honest': 'Ori ya faɗi haka maimakon yin zato',
    'card.refusedLabel': 'Babu auna',
    'card.refusedHeadline': 'Ori bai iya ganina a fili ba.',
    'card.lockedBody': 'An auna daga hasken da ya billo daga fatata — babu agogo, babu abin nannaɗewa, babu abin da ya taɓa ni. Ori yana ba da lamba ne kawai idan sassa masu zaman kansu da hanyoyi masu zaman kansu suka yarda.',
    'card.refusedBody': 'Maimakon buga lamba mai kuskure da tabbaci, Ori ya ƙi — ya kuma rubuta ƙin, domin a iya auna kuskuren a gyara shi maimakon a bar shi yana maimaituwa a shiru.',
    'card.disclaimer': 'Samfuri. Ba na’urar likita ba. Ba gano cuta ba.',

    'canvas.traceEmpty': 'hasken da ke dawowa daga fatarka',
    'canvas.signalReaches': 'kashi {percent}% na siginar bugun zuciya ke isa ga kyamara',
    'canvas.noReading': 'babu auna tukuna',
    'canvas.steady': '{bpm} bpm · daidai',
    'canvas.couldNotRead': 'bai iya karanta ka ba',

    'result.title': 'Aunarka',
    'result.found': 'an samu cikin daƙiƙa {seconds}',
    'result.none': 'babu sigina bayyananne',
    'result.confirmed':
      'An tabbatar ta hanyar auna sassa {regions} da hanyoyi masu zaman kansu {methods}, waɗanda suka yarda cikin {spread} bpm.',
    'result.refused':
      'Ori ya duba har ƙarshen lokacin bai kuma sami bugun zuciya da ya amince da shi ba. A wannan launin fata kusan kashi {attenuation}% na siginar bugun zuciya ne ke dawowa ga kyamara. Maimakon ya ba ka lamba da ba zai iya tsayawa a kanta ba, Ori yana gaya maka cewa bai iya ganin ka ba.',
    'result.conventional': 'Manhajar gargajiya da ta ba da rahoton {bpm} bpm daga wannan bayanin',
    'result.conventional.locked': '.',
    'result.conventional.failed': ', ba tare da wani gargaɗi ba. Wannan shi ne abin da ake sayarwa a yau.',

    'diag.toggle': 'Abin da Ori ke gani',
    'diag.empty': 'Babu komai tukuna. Ori na buƙatar kusan daƙiƙa takwas na bidiyo mai kyau kafin ya sami abin nunawa.',
    'diag.method': 'Hanya',
    'diag.reads': 'Ya karanta',
    'diag.from': 'Daga',
    'diag.method.pos': 'Launi (POS)',
    'diag.method.green': 'Tashar kore',
    'diag.regions': 'Sassa {count}',
    'diag.regions.one': 'Sashe 1',
    'diag.disagree':
      'Hanyoyin biyu sun bambanta da {spread} bpm. Wataƙila ɗayansu yana bin motsi ne maimakon bugun zuciya, don haka Ori ba zai ba da rahoton ko ɗaya ba.',
    'diag.agree': 'Hanyoyin biyu sun yarda cikin {spread} bpm.',
    'diag.framesUsed': 'Hotunan da aka yi amfani da su',
    'diag.harmonic': 'Goyon bayan harmonic',
    'diag.spread': 'Bambanci tsakanin sassa',
    'diag.perfusion': 'Gudanar jini',

    'consent.eyebrow': 'Abin da kake rabawa',
    'consent.title.lead': 'Maɓalli huɗu.',
    'consent.title.em': 'Duk a kashe har sai ka ce.',
    'consent.lede': 'Ana iya kashe kowanne ba tare da rasa tarihinka ba, kuma kowanne yana biyan ka muddin yana kunne.',
    'consent.offline.title': 'Babu haɗi',
    'consent.offline.body': 'Ba a iya isa ga sabis na Ori ba, don haka ba za a iya canza waɗannan maɓallan yanzu ba. Tarihinka yana lafiya a wannan na’urar, kuma ba a raba komai yayin da babu haɗi.',
    'consent.error.title': 'Bai ajiye ba',
    'consent.corpus.title': 'Taimaka a gyara na’urar',
    'consent.corpus.body': 'Kowace auna, ba tare da wani abu da ke nuna ko wanene kai ba, tana shiga tarin bayanan da ke koya wa waɗannan na’urori ganin fata mai duhu. Aunar da ta gaza ita ce mafi daraja gaba ɗaya.',
    'consent.corpus.earn': 'Kiredit 25 kan kowace auna',
    'consent.routing.title': 'Ba da shawarar inda zan je',
    'consent.routing.body': 'Bar Ori ya nuna maka dandalin OmniAlera da ya dace idan auna ta cancanci hakan. Ba a aika komai ko’ina — ana yin shawarar a cikin na’urarka.',
    'consent.routing.earn': 'Kyauta, kuma babu bayanin da ke fita',
    'consent.cohort.title': 'Shiga ƙungiyar bincike',
    'consent.cohort.body': 'Ka kasance cikin ƙungiyar dogon lokaci wadda ba a san sunan mahalarta ba, wadda abokan bincike da aka tantance za su iya nazarta. Ka fita a duk lokacin da ka ga dama: fita yana cire ka daga kowane cirewa na gaba, kuma ana sanar da abokan da suke riƙe da cirewar baya.',
    'consent.cohort.earn': 'Ƙarin kiredit 10 kan kowace auna',
    'consent.assistant.title': 'Bar Ori ya karanta tarihina',
    'consent.assistant.body': 'Ba da damar taƙaitaccen bayanin aunarka ya raka tambaya domin Ori ya amsa game da yanayinka. Ba a taɓa haɗa sunanka a ciki ba.',
    'consent.assistant.earn': 'Kyauta',
    'consent.earned.title': 'Abin da ka samu',
    'consent.earned.value': 'Kiredit {credits}',
    'consent.earned.body': 'Farashin misali ne. A aikace za a biya su ta kuɗin waya. Manufar ita ce a biya mai bayarwa, ba a ba wa kamfani kyauta don ya sake sayarwa ba.',
    'consent.withdraw.title': 'Yadda janyewa ke aiki da gaske',
    'consent.withdraw.body1': 'Kashe ƙungiyar bincike yana yin abubuwa uku, ba ɗaya ba. An cire ka daga kowane cirewa da za a gina daga yanzu. Duk cirewar da aka gina amma ba a karɓa ba tukuna tana cire layukanka kafin a ba da ita. Kuma ga cirewar da aka riga aka bayar, ana ba abokin da ke riƙe da ita sanarwa da ke nuna ka a matsayin wanda ya janye.',
    'consent.withdraw.body2': 'Na ukun shi ne ɓangaren da kusan babu wanda ke ginawa. Yardar da ba za ta iya kaiwa ga bayanan da suka riga suka fita ba ba yarda ba ce — takarda ce kawai.',

    'shared.why': 'Dalilin wannan',
    'shared.kept': 'An ajiye a wannan na’urar. Rabawa a kashe take.',
    'shared.keptNoService': 'An ajiye a wannan na’urar — ba a saita sabis na tarin bayanai ba.',
    'shared.sending': 'Ana rabawa…',
    'shared.added': 'An ƙara cikin tarin bayanai.',
    'shared.addedWithQueue': 'An ƙara cikin tarin bayanai, tare da auna {count} da suke jira.',
    'shared.queuedOffline':
      'An ajiye a wannan na’urar. Za a raba shi da zarar ka dawo kan layi ({count} suna jira).',
    'shared.refused': 'Sabis ɗin bai karɓi wannan aunar ba, don haka ba a raba komai ba.',
    'shared.flushed': 'Auna {count} da suke jira yanzu suna cikin tarin bayanai.',
    'shared.flushed.one': 'Auna 1 da take jira yanzu tana cikin tarin bayanai.',

    /* ── ra'ayoyi biyu ──────────────────────────────────────────────── */
    'view.group': 'Wa wannan aunawa take',
    'view.patient': 'Domin ni',
    'view.clinician': 'Domin likita',

    'floor.empty': 'Babu abin da za a zana tukuna.',
    'floor.bar': 'abin da dole ya wuce',
    'floor.axis': 'yiwuwar bugun zuciya',
    'floor.alt': 'Duk bugun da Ori ya duba, da matakin da dole ya kai kafin Ori ya bayar da shi.',
    'floor.caption.locked':
      'Dogon alamar shi ne bugun zuciyarka. Ya wuce layin da aka yi da digo sosai — wannan layin shi ne matakin da Ori ke bukata kafin ya faɗi komai.',
    'floor.caption.refused':
      'Babu abin da ya kai layin da aka yi da digo a nan. Wannan layin shi ne matakin da Ori ke bukata kafin ya bayar da adadi — don haka bai bayar ba.',

    'clin.locked': 'An kulle a {bpm} bpm bayan daƙiƙa {seconds}. Duk sharuɗɗa biyar sun cika.',
    'clin.refused': 'Babu aunawa. Ya faɗi a kan: {gates}.',
    'clin.refusedNoGate':
      'Babu aunawa. Lokacin ya ƙare kafin wani gwaji ya cika duk sharuɗɗa biyar.',
    'clin.region': 'Yanki',
    'clin.region.forehead': 'Goshi',
    'clin.region.leftCheek': 'Kunci na hagu',
    'clin.region.rightCheek': 'Kunci na dama',
    'clin.region.other': 'Yanki {n}',
    'clin.attenuation': 'Siginar da ta kai na’urar',
    'clin.engine': 'Na’urar bincike',
    'clin.conventional':
      'Na’urar aunawa ta gargajiya da ta bayar da {bpm} bpm daga wannan rikodin guda.',
    'clin.disclaimer':
      'Samfurin aiki. Ba na’urar likita ba ce, ba a tabbatar da ita da wata na’ura ta gaskiya ba, kuma ba tushen yanke shawarar likita ba ce.',

    'gate.header.criterion': 'Sharaɗi',
    'gate.header.measured': 'Abin da aka auna',
    'gate.header.required': 'Abin da ake bukata',
    'gate.prominence': 'Tsayin siginar',
    'gate.harmonic': 'Goyon bugu na biyu',
    'gate.stability': 'Kwanciyar hankali',
    'gate.agreement': 'Yarjejeniyar yankuna',
    'gate.methods': 'Bambancin hanyoyi',
    'gate.pass': 'ya wuce',
    'gate.fail': 'ya faɗi',
    'gate.skip': 'ba a duba ba',
    'gate.notAssessed': 'ba a iya dubawa — yanki {regions} ne kawai suka yi aiki',


    /* ── abin da za ka jira a wannan launin fata ────────────────────── */
    'tone.expect.strong':
      'A wannan launin fata siginar tana da ƙarfi. Ori zai iya karanta ka a yawancin lokuta.',
    'tone.expect.good':
      'A wannan launin fata siginar tana da kyau. Ori zai iya karanta ka a mafi yawan lokuta.',
    'tone.expect.weak':
      'A wannan launin fata siginar ba ta da ƙarfi. Sau da yawa Ori ba zai iya karanta ka ba — kuma zai faɗi haka, maimakon ya ba ka adadin da ba zai iya tsayawa a kai ba.',
    'tone.expect.veryWeak':
      'A wannan launin fata siginar ba ta da ƙarfi ko kaɗan, kuma sau da yawa Ori ba zai iya karanta ka ba ko kaɗan. Wannan shi ne gaskiyar iyakar abin da kyamara ke iya gani a yau. Duk ƙin da ka raba shi ne ke canza wannan.',

    'shared.notKept':
      'Ba a iya adana wannan aunawa a na’urarka ba. Ba a raba komai ba, aunawar kuma ta ɓace — yawanci wannan yanayin bincike ne na sirri ko kuma wurin ajiya ya cika.',

  },
};
